import { FC } from 'react';
import { SidePanel } from '@carbon/ibm-products';
import styles from 'pages/afe-catalog/structuring/structuring-module.module.scss';

// Mirror of WellScoringGlossaryPanel, for the Line Item Review drill-in. Documents
// the five variance % columns (computed live in variance.ts — the single source of
// truth for the math) plus the read-side "Decimal variance" badge. FE-only; no data.

interface Props {
  open: boolean;
  onRequestClose: () => void;
  // Shell root the slide-in SidePanel anchors its margin to (RC structuring shell).
  selectorPageContent?: string;
}

// name matches the drill-in table's column header verbatim.
const METRICS: { name: string; formula: string; desc: string }[] = [
  {
    name: 'Owner Net Value Variance %',
    formula:
      '(line_item_net_value − decimal × gross_net) ÷ line_item_net_value × 100',
    desc: 'Compares the stated Line Item Net Value against the decimal applied to Gross Net. A non-zero value means the net payment disagrees with what the decimal implies. Shows “--” when Line Item Net Value is missing or 0.',
  },
  {
    name: 'Dec Calc (Value) Variance %',
    formula: '(owner_value ÷ gross_value − decimal) × 100',
    desc: 'The decimal implied by the owner’s share of gross value, minus the stated Line Item Decimal. Highlights lines where the value split disagrees with the stated decimal. Shows “--” when Gross Value is missing or 0.',
  },
  {
    name: 'Dec Calc (Volume) Variance %',
    formula: '(owner_volume ÷ gross_volume − decimal) × 100',
    desc: 'The decimal implied by the owner’s share of gross volume, minus the stated Line Item Decimal. Uses Gross Volume (not Gross Value). Shows “--” on tax/deduction lines where Gross Volume is blank.',
  },
  {
    name: 'Gross Value Variance %',
    formula: '(gross_value − price × gross_volume) ÷ gross_value × 100',
    desc: 'Compares the stated Gross Value against Price × Gross Volume. A non-zero value flags a pricing or volume inconsistency. Shows “--” when Gross Value is missing or 0.',
  },
  {
    name: 'Gross Net Variance %',
    formula:
      '(gross_net − (gross_value + gross_taxes + gross_adjustments)) ÷ gross_net × 100',
    desc: 'Compares the stated Gross Net against Gross Value plus Gross Taxes and Gross Adjustments (both signed — not negated). Shows “--” when Gross Net is missing/0, or when neither Gross Taxes nor Gross Adjustments is itemized (some sources report tax only at the owner level, so there is nothing to reconcile against).',
  },
];

export const VarianceGlossaryPanel: FC<Props> = ({
  open,
  onRequestClose,
  selectorPageContent = '.rc-structuring-shell',
}) => (
  <SidePanel
    open={open}
    onRequestClose={onRequestClose}
    title="Variance Glossary"
    subtitle="How each line-item variance is calculated."
    slideIn
    selectorPageContent={selectorPageContent}
    placement="right"
    size="md"
  >
    <div className={styles.glossary}>
      <div className={styles.glossarySection}>
        <p className={styles.glossaryBody}>
          Each variance recomputes live from the values in this table, so
          editing a field updates it with no round-trip. A variance of 0.0%
          means the line is internally consistent; “--” means the calculation is
          undefined for that line (a missing or zero divisor).
        </p>
      </div>

      <hr className={styles.glossaryDivider} />

      <div className={styles.glossarySection}>
        <h4 className={styles.glossaryHeading}>Variance Columns</h4>
        <dl className={styles.glossaryFactors}>
          {METRICS.map((m) => (
            <div key={m.name} className={styles.glossaryFactor}>
              <div className={styles.glossaryFactorHeader}>
                <dt className={styles.glossaryFactorName}>{m.name}</dt>
              </div>
              <code className={styles.glossaryFormula}>{m.formula}</code>
              <dd className={styles.glossaryFactorDesc}>{m.desc}</dd>
            </div>
          ))}
        </dl>
      </div>

      <hr className={styles.glossaryDivider} />

      <div className={styles.glossarySection}>
        <h4 className={styles.glossaryHeading}>Decimal variance flag</h4>
        <p className={styles.glossaryBody}>
          On the Royalty Check list and detail tables, a line is tagged with a
          “Decimal variance” badge when its stated decimal disagrees with the
          decimal implied by volume by more than 0.001:
        </p>
        <code className={styles.glossaryFormula}>
          | owner_volume ÷ gross_volume − decimal | &gt; 0.001
        </code>
        <p className={styles.glossaryBody}>
          When volume or the decimal is missing, it falls back to the Dec Calc
          (Value / Volume) variance percentages, flagging the line when either
          exceeds a rounding epsilon.
        </p>
      </div>
    </div>
  </SidePanel>
);
